import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import Icons from "./Icons";

// Reveal-on-scroll wrapper
function Reveal({ children, delay = 0, as: Tag = "div", ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (r.top < vh * 0.95 && r.bottom > 0) {
      const t = setTimeout(() => setShown(true), delay);
      return () => clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setTimeout(() => setShown(true), delay);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${shown ? "in" : ""} ${rest.className || ""}`}
      style={rest.style}
    >
      {children}
    </Tag>
  );
}

// Card with cursor-following glow
function GlowCard({ children, className = "", ...rest }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <div
      ref={ref}
      className={`card ${className}`}
      onMouseMove={onMove}
      {...rest}
    >
      {children}
    </div>
  );
}

// =================== NAV ===================
export function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["Features", "#features"],
    ["Games", "#games"],
    ["Devices", "#devices"],
    ["Pricing", "#pricing"],
  ];

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        background: scrolled ? "rgba(7,6,11,0.72)" : "transparent",
        backdropFilter: scrolled ? "blur(14px) saturate(140%)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(14px) saturate(140%)" : "none",
        borderBottom: scrolled
          ? "1px solid var(--line)"
          : "1px solid transparent",
        transition: "background .25s ease, border-color .25s ease",
      }}
    >
      <div
        className="wrap"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 72,
        }}
      >
        <a
          href="#"
          style={{ display: "flex", alignItems: "center", gap: 10 }}
          aria-label="Game Accelerator"
        >
          <img
            src="/assets/logo.png"
            alt="Game Accelerator"
            style={{
              width: "auto",
              display: "block",
              filter: "drop-shadow(0 0 18px rgba(154,86,255,0.35))",
              height: "39px",
            }}
          />
        </a>

        <nav
          style={{ display: "flex", alignItems: "center", gap: 4 }}
          className="nav-links"
        >
          {links.map(([label, href]) => (
            <a
              key={label}
              href={href}
              style={{
                padding: "8px 14px",
                fontSize: 14,
                color: "var(--text-2)",
                borderRadius: 8,
                transition: "color .2s, background .2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--text)";
                e.currentTarget.style.background = "rgba(255,255,255,0.04)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--text-2)";
                e.currentTarget.style.background = "transparent";
              }}
            >
              {label}
            </a>
          ))}
        </nav>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <a href="#pricing" className="btn btn--primary btn--sm">
            Get Accelerator
          </a>
        </div>

        <button
          aria-label="Menu"
          onClick={() => setOpen(!open)}
          className="nav-burger"
          style={{
            display: "none",
            background: "transparent",
            border: "1px solid var(--line-strong)",
            borderRadius: 10,
            color: "var(--text)",
            width: 40,
            height: 40,
          }}
        >
          <span
            style={{
              display: "block",
              width: 16,
              height: 1.5,
              background: "currentColor",
              margin: "4px auto",
            }}
          />
          <span
            style={{
              display: "block",
              width: 16,
              height: 1.5,
              background: "currentColor",
              margin: "4px auto",
            }}
          />
          <span
            style={{
              display: "block",
              width: 16,
              height: 1.5,
              background: "currentColor",
              margin: "4px auto",
            }}
          />
        </button>
      </div>

      {open && (
        <div className="wrap" style={{ paddingBottom: 16 }}>
          <div
            style={{
              display: "grid",
              gap: 4,
              padding: 8,
              background: "var(--surface)",
              border: "1px solid var(--line)",
              borderRadius: 14,
            }}
          >
            {links.map(([label, href]) => (
              <a
                key={label}
                href={href}
                onClick={() => setOpen(false)}
                style={{
                  padding: "12px 14px",
                  borderRadius: 10,
                  color: "var(--text)",
                }}
              >
                {label}
              </a>
            ))}
            <a
              href="#pricing"
              onClick={() => setOpen(false)}
              className="btn btn--primary"
              style={{ marginTop: 6 }}
            >
              Get Accelerator
            </a>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .nav-links { display: none !important; }
          .nav-burger { display: block !important; }
          header .wrap > div:nth-child(3) > a { display: none; }
        }
      `}</style>
    </header>
  );
}

// =================== HERO ===================
export function Hero({ bgStyle = "orb" }) {
  const particles = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 9,
        duration: 7 + Math.random() * 6,
        size: 2 + Math.random() * 3,
      })),
    [],
  );

  const [ping, setPing] = useState(8);
  useEffect(() => {
    const id = setInterval(() => {
      setPing((p) =>
        Math.max(6, Math.min(11, p + (Math.random() > 0.5 ? 1 : -1))),
      );
    }, 900);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      style={{
        position: "relative",
        paddingTop: 160,
        paddingBottom: 120,
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: "50%",
          bottom: -200,
          transform: "translateX(-50%)",
          width: "min(1200px, 110%)",
          height: 640,
          background:
            "radial-gradient(closest-side, rgba(154,86,255,0.45), rgba(154,86,255,0.15) 55%, transparent 75%)",
          filter: "blur(80px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <div className="grid-bg" />
      <div className="hero-bg">
        {bgStyle !== "minimal" && <div className="orb" />}
        {bgStyle === "orb" && <div className="orb-2" />}
        {bgStyle === "particles" && (
          <div className="particles">
            {particles.map((p, i) => (
              <span
                key={i}
                style={{
                  left: `${p.left}%`,
                  bottom: 0,
                  width: p.size,
                  height: p.size,
                  animationDelay: `${p.delay}s`,
                  animationDuration: `${p.duration}s`,
                }}
              />
            ))}
          </div>
        )}
        <div className="scan" />
      </div>

      <div
        className="wrap"
        style={{ position: "relative", textAlign: "center", maxWidth: 1000 }}
      >
        <Reveal delay={80}>
          <h1
            style={{
              fontSize: "clamp(48px, 7.5vw, 96px)",
              fontWeight: 600,
              lineHeight: 0.98,
              letterSpacing: "-0.035em",
              margin: "24px 0 20px",
              textWrap: "balance",
            }}
          >
            Cut the lag.
            <br />
            <span
              style={{
                background:
                  "linear-gradient(180deg, #FFFFFF 0%, #B584FF 60%, #9A56FF 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                filter: "drop-shadow(0 0 30px rgba(154,86,255,0.25))",
              }}
            >
              Win the match.
            </span>
          </h1>
        </Reveal>

        <Reveal delay={140}>
          <p
            style={{
              fontSize: 19,
              color: "var(--text-2)",
              maxWidth: 620,
              margin: "0 auto 36px",
              lineHeight: 1.55,
            }}
          >
            Route your traffic through a private gaming backbone—lower ping,
            zero loss, peak performance.
          </p>
        </Reveal>

        <Reveal delay={200}>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <a href="#pricing" className="btn btn--primary btn--lg">
              Accelerate your game <Icons.ArrowRight size={16} />
            </a>
          </div>
        </Reveal>

        <Reveal delay={280}>
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--text-3)" }}>
            No credit card required · Cancel anytime
          </div>
        </Reveal>

        <Reveal delay={360}>
          <div
            style={{
              margin: "72px auto 0",
              maxWidth: 900,
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 0,
              padding: 20,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))",
              borderRadius: 18,
              backdropFilter: "blur(8px)",
            }}
            className="hero-stats"
          >
            {[
              {
                label: "Avg ping",
                value: (
                  <span>
                    <span className="tabular">{ping}</span>
                    <span
                      style={{
                        color: "var(--text-3)",
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                    >
                      {" "}
                      ms
                    </span>
                  </span>
                ),
              },
              { label: "Countries", value: <span>60</span> },
              {
                label: "Servers",
                value: (
                  <span>
                    2000
                    <span
                      style={{
                        color: "var(--text-3)",
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                    >
                      +
                    </span>
                  </span>
                ),
              },
              {
                label: "Active players",
                value: (
                  <span>
                    2.4
                    <span
                      style={{
                        color: "var(--text-3)",
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                    >
                      K
                    </span>
                  </span>
                ),
              },
            ].map((s, i) => (
              <div
                key={i}
                style={{
                  padding: "8px 16px",
                  borderLeft: i === 0 ? "none" : "1px solid var(--line)",
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "var(--text-3)",
                  }}
                >
                  {s.label}
                </div>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 600,
                    marginTop: 4,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {s.value}
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <style>{`
          @media (max-width: 700px) {
            .hero-stats { grid-template-columns: repeat(2, 1fr) !important; gap: 16px !important; }
            .hero-stats > div { border-left: none !important; }
          }
        `}</style>
      </div>
    </section>
  );
}

// =================== WHY ===================
export function Why() {
  const items = [
    {
      icon: <Icons.Activity size={22} />,
      title: "Up to 87% lower ping",
      text: "Smart route selection avoids ISP congestion and jumps straight to the game server.",
    },
    {
      icon: <Icons.Shield size={22} />,
      title: "DDoS-grade protection",
      text: "Stay anonymous in lobbies. Our private tunnel hides your IP from attackers.",
    },
    {
      icon: <Icons.Globe size={22} />,
      title: "Region unlock",
      text: "Connect to 180+ edge nodes across 6 continents — play with anyone, anywhere.",
    },
    {
      icon: <Icons.Bolt size={22} />,
      title: "Zero-config setup",
      text: "One-click acceleration. We auto-detect your game and pick the fastest route.",
    },
  ];

  return (
    <section className="section" id="why">
      <div className="wrap">
        <div className="section-head">
          <div className="copy">
            <Reveal delay={60}>
              <h2 className="section-title">
                Built for the milliseconds that matter.
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="section-sub">
                A win or a wipe is decided in 40ms windows. We give you the
                network that pros use, without the contract.
              </p>
            </Reveal>
          </div>
        </div>

        <div
          className="why-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 20,
          }}
        >
          {items.map((it, i) => (
            <Reveal key={i} delay={i * 80}>
              <div
                style={{
                  padding: "28px 24px",
                  borderTop: "1px solid var(--line-strong)",
                  position: "relative",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: -1,
                    left: 0,
                    width: 40,
                    height: 1,
                    background: "var(--accent)",
                    boxShadow: "0 0 12px var(--accent-glow)",
                  }}
                />
                <div className="glow-chip" style={{ marginBottom: 20 }}>
                  {it.icon}
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    marginBottom: 8,
                  }}
                >
                  {it.title}
                </div>
                <div
                  style={{
                    fontSize: 14.5,
                    color: "var(--text-2)",
                    lineHeight: 1.55,
                  }}
                >
                  {it.text}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <style>{`
          @media (max-width: 900px) { .why-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          @media (max-width: 540px) { .why-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </div>
    </section>
  );
}

// =================== FEATURES (6 cards) ===================
export function Features() {
  const items = [
    {
      icon: <Icons.Wifi size={22} />,
      title: "Smart Route AI",
      text: "Reinforcement-learning agent picks the optimal path every 200ms, in real time.",
    },
    {
      icon: <Icons.Cpu size={22} />,
      title: "FPS Boost Engine",
      text: "Frees CPU cycles by suspending background apps and tuning your power profile.",
    },
    {
      icon: <Icons.Layers size={22} />,
      title: "Dual-tunnel failover",
      text: "Two parallel routes. If one drops a packet, the other delivers it. You never notice.",
    },
    {
      icon: <Icons.Shield size={22} />,
      title: "IP cloak",
      text: "Private gateway hides your real IP from snipers, swatters, and stream-snipers.",
    },
    {
      icon: <Icons.Activity size={22} />,
      title: "Live network HUD",
      text: "In-game overlay shows ping, jitter, loss, and frame pacing. Spot issues instantly.",
    },
    {
      icon: <Icons.Settings size={22} />,
      title: "Per-game profiles",
      text: "Auto-applies your tuned settings the moment a supported title launches.",
    },
  ];

  return (
    <section className="section" id="features">
      <div className="wrap">
        <div className="section-head">
          <div className="copy">
            <Reveal delay={60}>
              <h2 className="section-title">Six edges, one install.</h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="section-sub">
                Everything works out of the box. Power users can drill into
                per-game profiles, custom routes, and live telemetry.
              </p>
            </Reveal>
          </div>
        </div>

        <div
          className="feat-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 20,
          }}
        >
          {items.map((it, i) => (
            <Reveal key={i} delay={i * 60}>
              <GlowCard>
                <div className="glow-chip" style={{ marginBottom: 22 }}>
                  {it.icon}
                </div>
                <div
                  style={{
                    fontSize: 19,
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    marginBottom: 8,
                  }}
                >
                  {it.title}
                </div>
                <div
                  style={{
                    fontSize: 14.5,
                    color: "var(--text-2)",
                    lineHeight: 1.55,
                  }}
                >
                  {it.text}
                </div>
              </GlowCard>
            </Reveal>
          ))}
        </div>
        <style>{`
          @media (max-width: 900px) { .feat-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          @media (max-width: 600px) { .feat-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </div>
    </section>
  );
}

// =================== GAMES ===================
export function Games() {
  const games = [
    "PUBG Mobile",
    "Call of Duty: Mobile",
    "Mobile Legends",
    "Roblox",
    "League of Legends: Wild Rift",
  ];

  return (
    <section className="section section--tight" id="games">
      <div className="wrap">
        <div className="section-head">
          <div className="copy">
            <Reveal delay={60}>
              <h2 className="section-title">Tuned for every battlefield.</h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="section-sub">
                We've optimized your games—just start Game Accelerator and play
                smoother with lower ping.
              </p>
            </Reveal>
          </div>
        </div>

        <div
          className="games-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 14,
            alignItems: "stretch",
          }}
        >
          {games.map((name, i) => (
            <Reveal key={name} delay={i * 20}>
              <GlowCard
                style={{
                  padding: "22px 18px",
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 88,
                }}
              >
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {name}
                </div>
              </GlowCard>
            </Reveal>
          ))}
        </div>
        <style>{`
          @media (max-width: 1000px) { .games-grid { grid-template-columns: repeat(4, 1fr) !important; } }
          @media (max-width: 700px)  { .games-grid { grid-template-columns: repeat(3, 1fr) !important; } }
          @media (max-width: 480px)  { .games-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        `}</style>
      </div>
    </section>
  );
}

// =================== DEVICES ===================
export function Devices() {
  const list = [
    { icon: <Icons.Apple size={32} />, name: "iOS", sub: "Coming soon" },
    { icon: <Icons.Android size={32} />, name: "Android", sub: "Coming soon" },
  ];

  return (
    <section className="section" id="devices">
      <div className="wrap">
        <div
          style={{
            padding: "14px 14px 14px 48px",
            borderRadius: 28,
            background:
              "radial-gradient(800px 400px at 80% 0%, rgba(154,86,255,0.18), transparent 60%), linear-gradient(180deg, var(--surface), var(--bg-1))",
            display: "grid",
            gridTemplateColumns: "1.1fr 1fr",
            gap: 48,
            alignItems: "stretch",
          }}
          className="devices-card"
        >
          <div style={{ alignSelf: "center", padding: "42px 0" }}>
            <Reveal delay={60}>
              <h2 className="section-title" style={{ marginTop: 16 }}>
                One account for
                <br />
                Every device.
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="section-sub">
                Use on up to 5 devices—settings sync instantly across PC,
                console, and mobile.
              </p>
            </Reveal>
          </div>

          <div
            className="devices-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
              alignSelf: "stretch",
            }}
          >
            {list.map((d, i) => (
              <Reveal key={d.name} delay={i * 60}>
                <div
                  style={{
                    position: "relative",
                    padding: 22,
                    borderRadius: 16,
                    background: "rgba(255,255,255,0.025)",
                    transition: "background .2s",
                    minHeight: 200,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "287.375px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(154,86,255,0.35)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "var(--line)";
                  }}
                >
                  <a
                    href="#"
                    aria-label={`Download for ${d.name}`}
                    style={{
                      position: "absolute",
                      bottom: 14,
                      right: 14,
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      display: "grid",
                      placeItems: "center",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      color: "var(--text)",
                      transition: "all .2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--accent)";
                      e.currentTarget.style.color = "#fff";
                      e.currentTarget.style.borderColor = "transparent";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "rgba(255,255,255,0.04)";
                      e.currentTarget.style.color = "var(--text)";
                      e.currentTarget.style.borderColor =
                        "rgba(255,255,255,0.08)";
                    }}
                  >
                    <Icons.Download size={14} />
                  </a>
                  <div style={{ color: "var(--text)" }}>{d.icon}</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>
                      {d.name}
                    </div>
                    <div
                      style={{
                        fontSize: 12.5,
                        color: "var(--text-3)",
                        marginTop: 2,
                      }}
                    >
                      {d.sub}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) {
            .devices-card { grid-template-columns: 1fr !important; padding: 28px !important; }
          }
        `}</style>
      </div>
    </section>
  );
}

// =================== PRICING ===================
export function Pricing({ defaultBilling = "yearly" }) {
  const [billing, setBilling] = useState(defaultBilling);
  const yearly = billing === "yearly";

  const plans = [
    {
      name: "Starter",
      tag: "For casual players",
      price: yearly ? 4 : 6,
      features: [
        "1 device",
        "40+ edge nodes",
        "Smart routing",
        "Basic FPS boost",
        "Community support",
      ],
      cta: "Get Now",
      featured: false,
    },
    {
      name: "Pro",
      tag: "Most popular",
      price: yearly ? 3.5 : 12,
      yearlyTotal: 41.99,
      savePct: yearly ? 71 : null,
      features: [
        "3 devices",
        "All 180+ edge nodes",
        "Dual-tunnel failover",
        "Advanced FPS boost",
        "Live network HUD",
        "Priority support",
      ],
      cta: "Get Pro",
      featured: true,
    },
    {
      name: "Elite",
      tag: "For squads & streamers",
      price: yearly ? 9.99 : 24,
      oldPrice: yearly ? 12 : null,
      features: [
        "5 devices",
        "Private edge nodes",
        "IP cloak & DDoS shield",
        "Custom routes & API",
        "Squad analytics",
        "24/7 white-glove support",
      ],
      cta: "Get Now",
      featured: false,
    },
  ];

  return (
    <section className="section" id="pricing">
      <div className="wrap">
        <div
          style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 48px" }}
        >
          <Reveal delay={60}>
            <h2 className="section-title">Unlock Your Performance Tier</h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="section-sub" style={{ margin: "0 auto" }}>
              Cancel anytime. 30-day money-back guarantee. No usage caps, ever.
            </p>
          </Reveal>

          <Reveal delay={180}>
            <div
              style={{
                display: "inline-flex",
                marginTop: 28,
                background: "var(--surface)",
                border: "1px solid var(--line-strong)",
                borderRadius: 999,
                padding: 4,
              }}
            >
              {[
                ["monthly", "Monthly"],
                ["yearly", "Yearly · save 33%"],
              ].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setBilling(k)}
                  style={{
                    padding: "8px 18px",
                    border: "none",
                    borderRadius: 999,
                    background: billing === k ? "var(--accent)" : "transparent",
                    color: billing === k ? "#fff" : "var(--text-2)",
                    fontSize: 13,
                    fontWeight: 500,
                    transition: "all .2s",
                    boxShadow:
                      billing === k
                        ? "0 4px 16px -4px var(--accent-glow)"
                        : "none",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div
          className="price-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 20,
            alignItems: "stretch",
          }}
        >
          {plans.map((p, i) => (
            <Reveal key={p.name} delay={i * 80}>
              <div
                style={{
                  position: "relative",
                  padding: 32,
                  borderRadius: 22,
                  background: p.featured
                    ? "linear-gradient(180deg, rgba(154,86,255,0.14), rgba(154,86,255,0.02)), var(--surface)"
                    : "var(--surface)",
                  border: p.featured
                    ? "1px solid rgba(154,86,255,0.55)"
                    : "1px solid transparent",
                  boxShadow: p.featured
                    ? "0 30px 80px -30px var(--accent-glow), 0 0 0 1px rgba(154,86,255,0.25)"
                    : "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 24,
                  height: "100%",
                }}
              >
                {p.featured && (
                  <span
                    style={{
                      position: "absolute",
                      top: -12,
                      left: 32,
                      padding: "4px 12px",
                      fontSize: 11,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      fontWeight: 600,
                      background: "var(--accent)",
                      color: "#fff",
                      borderRadius: 999,
                      boxShadow: "0 8px 24px -8px var(--accent-glow)",
                    }}
                  >
                    Recommended
                  </span>
                )}

                {p.featured && yearly && p.savePct && (
                  <span
                    style={{
                      position: "absolute",
                      top: 20,
                      right: 20,
                      padding: "5px 11px",
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      background: "rgba(58, 211, 140, 0.14)",
                      color: "#3AD38C",
                      borderRadius: 999,
                    }}
                  >
                    SAVE {p.savePct}%
                  </span>
                )}

                <div>
                  <div
                    style={{
                      fontSize: 14,
                      color: "var(--text-3)",
                      marginBottom: 6,
                    }}
                  >
                    {p.tag}
                  </div>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 600,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {p.name}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 56,
                      fontWeight: 600,
                      letterSpacing: "-0.03em",
                    }}
                    className="tabular"
                  >
                    ${p.price}
                  </span>
                  {p.oldPrice && (
                    <span
                      style={{
                        fontSize: 22,
                        color: "var(--text-3)",
                        textDecoration: "line-through",
                      }}
                      className="tabular"
                    >
                      ${p.oldPrice}
                    </span>
                  )}
                  <span style={{ color: "var(--text-3)", fontSize: 14 }}>
                    / month{yearly ? ", billed yearly" : ""}
                  </span>
                  {p.featured && yearly && p.yearlyTotal && (
                    <div
                      style={{
                        flexBasis: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginTop: 6,
                      }}
                    >
                      <span
                        style={{ fontSize: 13, color: "var(--text-2)" }}
                        className="tabular"
                      >
                        ${p.yearlyTotal} billed yearly
                      </span>
                    </div>
                  )}
                </div>

                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  {p.features.map((f) => (
                    <li
                      key={f}
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        fontSize: 14,
                        color: "var(--text)",
                      }}
                    >
                      <span
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 999,
                          background: p.featured
                            ? "rgba(154,86,255,0.2)"
                            : "rgba(255,255,255,0.05)",
                          color: p.featured
                            ? "var(--accent-2)"
                            : "var(--text-2)",
                          display: "grid",
                          placeItems: "center",
                          flex: "none",
                        }}
                      >
                        <Icons.Check size={11} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <a
                  href="#"
                  className={`btn ${p.featured ? "btn--primary" : "btn--ghost"}`}
                  style={{ marginTop: "auto", width: "100%" }}
                >
                  {p.cta}
                </a>
              </div>
            </Reveal>
          ))}
        </div>
        <style>{`
          @media (max-width: 900px) { .price-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </div>
    </section>
  );
}

// =================== CTA strip ===================
export function CTAStrip() {
  return (
    <section className="section section--tight">
      <div className="wrap">
        <Reveal>
          <div
            style={{
              position: "relative",
              padding: "64px 24px",
              textAlign: "center",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: "min(900px, 90%)",
                height: 360,
                background:
                  "radial-gradient(closest-side, rgba(154,86,255,0.45), rgba(154,86,255,0.15) 55%, transparent 75%)",
                filter: "blur(60px)",
                pointerEvents: "none",
                zIndex: 0,
              }}
            />
            <div style={{ position: "relative", zIndex: 1 }}>
              <h2 className="section-title" style={{ marginTop: 0 }}>
                Ready when you are.
              </h2>
              <p className="section-sub" style={{ margin: "0 auto 28px" }}>
                Install in 90 seconds. Feel the difference in your next match.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <a href="#pricing" className="btn btn--primary btn--lg">
                  Accelerate your game <Icons.ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// =================== FOOTER ===================
export function Footer() {
  const cols = [
    {
      title: "Product",
      links: [
        ["Features", "#features"],
        ["Games", "#games"],
        ["Devices", "#devices"],
        ["Pricing", "#pricing"],
      ],
    },
    {
      title: "Company",
      links: [
        ["Terms", "/terms"],
        ["Privacy", "/privacy"],
      ],
    },
  ];

  const linkStyle = { color: "var(--text-2)", fontSize: 14 };
  const onEnter = (e) => (e.currentTarget.style.color = "var(--text)");
  const onLeave = (e) => (e.currentTarget.style.color = "var(--text-2)");

  return (
    <footer
      style={{
        borderTop: "1px solid var(--line)",
        paddingTop: 72,
        paddingBottom: 36,
      }}
    >
      <div className="wrap">
        <div
          className="foot-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto auto",
            columnGap: 80,
            rowGap: 40,
            marginBottom: 56,
            alignItems: "start",
          }}
        >
          <div style={{ maxWidth: 560 }}>
            <a
              href="#"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 20,
              }}
              aria-label="Game Accelerator"
            >
              <img
                src="/assets/logo.png"
                alt="Game Accelerator"
                style={{ height: 30, width: "auto", display: "block" }}
              />
            </a>
            <p
              style={{
                color: "var(--text-3)",
                fontSize: 15,
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              The private gaming backbone built for competitive play. Smart
              routing, dual-tunnel failover, and DDoS-grade protection across
              2000+ servers in 60 countries. Lower ping, zero packet loss, every
              single match.
            </p>
          </div>

          {cols.map((c) => (
            <div key={c.title}>
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "var(--text-3)",
                  marginBottom: 16,
                }}
              >
                {c.title}
              </div>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "grid",
                  gap: 10,
                }}
              >
                {c.links.map(([label, href]) => (
                  <li key={label}>
                    {href.startsWith("/") ? (
                      <Link
                        to={href}
                        style={linkStyle}
                        onMouseEnter={onEnter}
                        onMouseLeave={onLeave}
                      >
                        {label}
                      </Link>
                    ) : (
                      <a
                        href={href}
                        style={linkStyle}
                        onMouseEnter={onEnter}
                        onMouseLeave={onLeave}
                      >
                        {label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <hr className="hr-soft" />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: 28,
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div
            style={{
              fontSize: 13,
              color: "var(--text-3)",
              textAlign: "center",
            }}
          >
            © 2026 AccelBoost · All rights reserved.
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) { .foot-grid { grid-template-columns: 1fr 1fr !important; column-gap: 40px !important; } }
          @media (max-width: 540px) { .foot-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </div>
    </footer>
  );
}
